import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks';
import { scrollToY } from '../utils/scrollToSection';
import { trudymorgan } from '../assets/images/images';

// Each project can take an `outcome`: one line on the result
// (e.g. "Cut loan approval from days to minutes"). It's shown under the
// name when present.
export const PROJECTS = [
  {
      id: 'davetsolutions',
      name: 'Dave-T Solutions',
      link: 'https://davetsolutions.com/',
      tech: 'React / Typescript',
      description: `Dave-T Solutions is a technology solutions company website. Built with React and TypeScript to deliver a robust and maintainable codebase.

The platform highlights the company's technical expertise, service offerings, and project portfolio, serving as a hub for client engagement and business development.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1766949529/davet_oiu9fw_zwteph.mp4',
      status: 'Completed',
      year: ''
    },
    {
      id: 'stitchhouse',
      name: 'Stitchhouse',
      link: 'https://www.thestitchouse.com/',
      tech: 'React / Typescript / Rest API',
      description: `Stitchhouse is a fashion marketplace designed to connect buyers and sellers in a vibrant online environment. Built with React for a responsive user interface and TypeScript for better maintainability, Stitchhouse offers curated product listings, secure payment processing, and order tracking.

The platform empowers small businesses and independent designers, providing them a space to showcase their collections while delivering a seamless shopping experience for fashion enthusiasts.`,
     image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1766949531/stitchhouse_fzk7ot.mp4',
      status: 'Completed'
    },
    {
      id: 'wafeo',
      name: 'West African Federation of Engineering Organisations',
      link: 'https://wafeo-official.com/',
      tech: 'React / Next.js',
      description: `The official website of the West African Federation of Engineering Organisations (WAFEO). Built with React and Next.js for fast, search-friendly pages.

The platform presents the federation, its member engineering bodies across West Africa, and its news and events, giving the regional engineering community a single, modern home online.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1767004679/screen-capture_q6ehq3.mp4',
      status: 'Completed',
      year: ''
    },
    {
      id: 'trudymorgan',
      name: 'Trudy Morgan',
      link: 'https://trudymorgan.com/',
      tech: 'React / Next.js',
      description: `Trudy Morgan is a personal portfolio and professional website. Built with React and Next.js for optimal performance and SEO.

The website showcases professional work, expertise, and provides a platform for connecting with clients and collaborators in a clean, modern interface.`,
      image: trudymorgan,
      status: 'Completed',
      year: ''
    },
    {
      id: 'hrms',
      name: 'HRMS',
      link: 'https://hrms.enyata.com/',
      tech: 'React / GraphQL',
      description: `Human Resource Management System built with React and GraphQL. HRMS is a comprehensive system designed to streamline HR processes. Built with React for a dynamic and responsive user interface and powered by GraphQL for efficient data querying.
HRMS offers features such as employee records management, payroll processing, performance evaluations, and benefits administration. It aims to simplify HR tasks, improve data accuracy, and enhance overall organizational productivity.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1766949530/HRMS_qhzcat.mp4', // Replace with actual screenshot
      status: 'Completed',
      year: ''
    },
    {
      id: 'kreditafrica',
      name: 'KreditAfrica',
      link: 'https://www.kreditafrica.com/',
      tech: 'Next.js / Typescript / PostgreSQL',
      description: `KreditAfrica is a fintech platform that facilitates the management of loans and credits. Utilizing Next.js for server-side rendering and enhanced performance, Typescript for type safety and maintainable code, and PostgreSQL for robust database management.

KreditAfrica provides users with secure and efficient tools to apply for loans, track their credit scores, and manage repayments. The platform aims to improve financial inclusion and provide accessible credit solutions to underserved populations.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1766949530/kreditAfrica_ppka9s_urutag.mp4',
      status: 'Completed',
      year: ''
    },
    {
      id: 'richbaychem',
      name: 'Richbay Chemicals Ghana',
      link: 'https://richbaychem.com/',
      tech: 'React / Typescript',
      description: `RichBay Chemical Ghana is a corporate website for a chemical manufacturing and distribution company. Built with React and TypeScript for a professional and responsive interface.

The platform showcases the company's products, services, and expertise in the chemical industry, providing detailed information about their offerings and facilitating business connections.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/video/upload/v1766949530/richbay_hqkujq_tve1uu.mp4',
      status: 'Completed',
      year: ''
    },
    {
      id: 'fluxtribe',
      name: 'FluxTribe',
      link: 'https://merifot.enyata.com/',
      tech: 'React / GraphQL',
      description: `FluxTribe is a social platform designed for collaboration on personal and group goals. Leveraging React to create a seamless and interactive user experience, and GraphQL to handle complex data interactions.

FluxTribe enables users to set, track, and achieve their goals through community support and collaboration. Users can share progress, provide encouragement, and collaborate on joint goals, fostering a supportive environment to help each other succeed.`,
      image: 'https://res.cloudinary.com/daj0ik8rv/image/upload/v1766949813/Fluxtribe_c5iqir.png',
    },
];

const pad = (n) => String(n).padStart(2, '0');
const isVideoUrl = (url) => !!url && /\.(mp4|webm|mov|avi|mkv)$/i.test(url);
const isCloudinaryVideo = (url) => /res\.cloudinary\.com\/[^/]+\/video\/upload\//.test(url);

// Cloudinary serves a compressed, right-sized version (~7x smaller than the
// originals) when asked for one in the URL.
const videoSrc = (url) =>
  isCloudinaryVideo(url) ? url.replace('/video/upload/', '/video/upload/q_auto,vc_auto,w_1280/') : url;

// A still of the first frame, shown until the video itself has loaded.
const videoPoster = (url) =>
  isCloudinaryVideo(url)
    ? url.replace('/video/upload/', '/video/upload/so_0,q_auto,f_auto,w_1280/').replace(/\.(mp4|webm|mov)$/i, '.jpg')
    : undefined;
const blockSave = {
  onContextMenu: (e) => e.preventDefault(),
  onDragStart: (e) => e.preventDefault(),
  draggable: 'false',
};

// Play only the videos whose index is in `playing`. Starts after a short
// pause so scrolling quickly past cards doesn't start and stop each one.
const usePlayOnly = (videoRefs, playing) => {
  const key = [...playing].sort().join(',');
  useEffect(() => {
    const wanted = new Set(key ? key.split(',').map(Number) : []);
    videoRefs.current.forEach((video, i) => {
      if (video && !wanted.has(i) && !video.paused) video.pause();
    });
    const timer = setTimeout(() => {
      wanted.forEach((i) => {
        const video = videoRefs.current[i];
        if (video && video.paused) video.play().catch(() => {});
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [videoRefs, key]);
};

// Load the cards around the active one; leave the rest until needed.
const preloadFor = (i, active) => (active !== null && Math.abs(i - active) <= 1 ? 'auto' : 'none');

const ProjectCard = ({ project, index, active, videoRef, preload = 'metadata', ...rest }) => (
  <a
    href={project.link}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`${project.name} (opens in a new tab)`}
    className="group block"
    {...rest}
  >
    <div className="relative aspect-[16/9] mb-6 md:mb-8 overflow-hidden bg-black/40">
      {isVideoUrl(project.image) ? (
        <video
          ref={videoRef}
          src={videoSrc(project.image)}
          poster={videoPoster(project.image)}
          aria-hidden="true"
          className={`w-full h-full object-cover transition-all duration-700 group-hover:grayscale-0 group-hover:scale-[1.03] select-none ${active ? 'grayscale-0' : 'grayscale'}`}
          muted
          loop
          playsInline
          preload={preload}
          {...blockSave}
        />
      ) : (
        <img
          src={project.image}
          loading="lazy"
          decoding="async"
          alt={`${project.name} website`}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:grayscale-0 group-hover:scale-[1.03] select-none ${active ? 'grayscale-0' : 'grayscale'}`}
          {...blockSave}
        />
      )}
      <span
        className={`absolute bottom-4 right-4 z-10 bg-black/70 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-white transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
      >
        Visit site ↗
      </span>
    </div>
    <div className="flex justify-between items-start gap-6 text-white">
      <div className="min-w-0">
        <p className={`eyebrow eyebrow-on-dark mb-2 transition-colors duration-500 group-hover:text-[#ff6347] ${active ? '!text-[#ff6347]' : ''}`}>{project.tech}</p>
        <h3 className="font-grandslang text-2xl md:text-4xl leading-tight">{project.name}</h3>
        {project.outcome && (
          <p className="mt-3 max-w-xl text-base md:text-lg font-light leading-relaxed text-white/70">{project.outcome}</p>
        )}
      </div>
      <span aria-hidden="true" className="font-grandslang text-4xl md:text-5xl leading-none text-[#ff6347]">{pad(index + 1)}</span>
    </div>
  </a>
);

const SectionLabel = () => (
  <p className="eyebrow eyebrow-on-dark">Selected Work</p>
);

/* ---------------------------------------------------------------------------
   Desktop: pinned horizontal rail. Each project holds still, centred, for a
   stretch of scrolling, then eases across to the next, so the rail always
   comes to rest on a whole project rather than halfway between two.
--------------------------------------------------------------------------- */
const DWELL = 0.3; // share of each step (at each end) spent holding still

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Where along the rail we are, as a fractional project index (e.g. 2.4).
const railPosition = (progress, count) => {
  if (count < 2) return 0;
  const segment = Math.min(progress * (count - 1), count - 1);
  const i = Math.min(Math.floor(segment), count - 2);
  const f = segment - i;
  const t = Math.max(0, Math.min(1, (f - DWELL) / (1 - 2 * DWELL)));
  return i + easeInOutCubic(t);
};

const ProjectRail = () => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const barRef = useRef(null);
  const cardRefs = useRef([]);
  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // The rail is moved directly from the scroll handler instead of through
  // React state, so scrolling doesn't re-render eight video cards per frame.
  // React only re-renders when the active project changes.
  useEffect(() => {
    let layout = null;

    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      const viewport = document.documentElement.clientWidth;
      const maxShift = Math.max(0, track.scrollWidth - viewport);
      // Resting shift for each card: centred, except the first sits on the
      // left margin and the last on the right margin.
      layout = cardRefs.current.map((c) =>
        c ? Math.max(0, Math.min(maxShift, c.offsetLeft + c.offsetWidth / 2 - viewport / 2)) : 0
      );
    };

    const update = () => {
      const el = containerRef.current;
      if (!el || !layout) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrollable = rect.height - vh;
      // Always computed and clamped (even off-screen) so jumping over or into
      // the section from the nav never leaves a stale position behind.
      const progress = scrollable > 0 ? Math.max(0, Math.min(1, -rect.top / scrollable)) : 0;
      const position = railPosition(progress, PROJECTS.length);

      const i = Math.floor(position);
      const next = Math.min(i + 1, layout.length - 1);
      const shift = layout[i] + (layout[next] - layout[i]) * (position - i);

      trackRef.current.style.transform = `translate3d(${-shift}px, 0, 0)`;
      if (barRef.current) barRef.current.style.transform = `scaleX(${progress})`;
      setActiveIndex(Math.round(position));
      setIsInView(rect.top < vh / 2 && rect.bottom > vh / 2);
    };

    const onResize = () => { measure(); update(); };

    onResize();
    document.fonts?.ready.then(onResize);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const playing = new Set();
  if (isInView) playing.add(activeIndex);
  if (hoveredIndex !== null) playing.add(hoveredIndex);
  usePlayOnly(videoRefs, playing);

  return (
    <section
      ref={containerRef}
      id="projects"
      data-layout="rail"
      className="bg-charcoal relative"
      style={{ height: `${(PROJECTS.length - 1) * 70 + 100}vh` }}
    >
      <div className="sticky top-0 hero-height overflow-hidden flex flex-col justify-center">
        <div className="absolute top-12 left-[8vw] right-[8vw] flex justify-between items-baseline">
          <SectionLabel />
          <p className="eyebrow eyebrow-on-dark tabular-nums" aria-live="polite">
            <span className="text-white">{pad(activeIndex + 1)}</span> / {pad(PROJECTS.length)}
          </p>
        </div>

        <div ref={trackRef} className="flex gap-[6vw] px-[8vw] w-max will-change-transform">
          {PROJECTS.map((p, i) => {
            const active = i === activeIndex;
            const lit = active || hoveredIndex === i;
            return (
              <div
                key={p.id}
                ref={(el) => (cardRefs.current[i] = el)}
                className="w-[70vw] lg:w-[52vw] flex-shrink-0"
                style={{
                  opacity: lit ? 1 : 0.35,
                  transform: `translateY(${lit ? 0 : 20}px)`,
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                }}
              >
                <ProjectCard
                  project={p}
                  index={i}
                  active={active}
                  preload={preloadFor(i, activeIndex)}
                  videoRef={(el) => (videoRefs.current[i] = el)}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              </div>
            );
          })}
        </div>

        {/* Progress through the work */}
        <div className="absolute bottom-12 left-[8vw] right-[8vw] h-px bg-white/15">
          <div ref={barRef} className="h-full bg-[#ff6347] origin-left" style={{ transform: 'scaleX(0)' }} />
        </div>
      </div>
    </section>
  );
};

/* ---------------------------------------------------------------------------
   Mobile: a plain vertical list. Whichever card is mostly on screen plays in
   colour (phones can't hover).
--------------------------------------------------------------------------- */
const ProjectList = () => {
  const cardRefs = useRef([]);
  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => ratios.set(Number(e.target.dataset.index), e.intersectionRatio));
        let best = null;
        let bestRatio = 0.5; // must be at least half on screen
        ratios.forEach((r, i) => {
          if (r > bestRatio) { best = i; bestRatio = r; }
        });
        setActiveIndex(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  usePlayOnly(videoRefs, new Set(activeIndex === null ? [] : [activeIndex]));

  return (
    <section id="projects" className="bg-charcoal px-5 py-24">
      <div className="mb-14">
        <SectionLabel />
        <h2 className="font-grandslang text-5xl text-white mt-4">Things I've built.</h2>
      </div>
      <div className="space-y-20">
        {PROJECTS.map((p, i) => (
          <div key={p.id} id={`project-${p.id}`} ref={(el) => (cardRefs.current[i] = el)} data-index={i}>
            <ProjectCard
              project={p}
              index={i}
              active={i === activeIndex}
              preload={preloadFor(i, activeIndex ?? 0)}
              videoRef={(el) => (videoRefs.current[i] = el)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

// Bring a given project into view: centred on the desktop rail, or scrolled
// to in the mobile list.
export const scrollToProject = (id) => {
  const index = PROJECTS.findIndex((p) => p.id === id);
  const section = document.getElementById('projects');
  if (index < 0 || !section) return;

  if (section.dataset.layout === 'rail') {
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const scrollable = section.offsetHeight - window.innerHeight;
    const share = PROJECTS.length > 1 ? index / (PROJECTS.length - 1) : 0;
    scrollToY(sectionTop + share * scrollable);
  } else {
    const card = document.getElementById(`project-${id}`);
    if (card) scrollToY(card.getBoundingClientRect().top + window.scrollY - 96);
  }
};

export const WorksSection = () => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  return isMobile ? <ProjectList /> : <ProjectRail />;
};

export default WorksSection;
